import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { CustomerProvider } from './contexts/CustomerContext';
import Home from './components/home/Home';
import LoginScreen from './components/auth/LoginScreen';
import TrialRegistration from './components/auth/TrialRegistration';
import PublicCustomerRegistration from './components/auth/PublicCustomerRegistration';
import PublicTestPage from './components/auth/PublicTestPage';
import ReferralSystemTest from './components/test/ReferralSystemTest';
import ForcePasswordChange from './components/auth/ForcePasswordChange';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/layout/ProtectedRoute';
import CustomerAuth from './components/customer/CustomerAuth';
import CustomerDashboard from './components/customer/CustomerDashboard';
import CustomerProtectedRoute from './components/customer/CustomerProtectedRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import CompaniesManager from './components/admin/CompaniesManager';
import AdminHistory from './components/admin/AdminHistory';
import AdminPointsView from './components/admin/AdminPointsView';
import AdminSettings from './components/admin/AdminSettings';
import CompanyDashboard from './components/company/CompanyDashboard';
import CustomersManager from './components/company/CustomersManager';
import PointsSearch from './components/company/PointsSearch';
import PointsHistory from './components/company/PointsHistory';
import AddPointsPage from './components/company/AddPointsPage';
import RewardsManager from './components/company/RewardsManager';
import RewardsHistory from './components/company/RewardsHistory';
import RedemptionsManager from './components/company/RedemptionsManager';
import RentalManager from './components/company/RentalManager';
import CustomerPointsHistory from './components/company/CustomerPointsHistory';
import ForceRedemption from './components/company/ForceRedemption';
import CompanySettings from './components/company/CompanySettings';
import SMSSettings from './components/admin/SMSSettings';
import { useAuth } from './contexts/AuthContext';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { useTrialExpiration } from './hooks/useTrialExpiration';

// Componente interno que usa o AuthContext
const AppContent = () => {
  const { user, refreshUser } = useAuth();
  
  // Hook para controle de sessão (30 minutos de inatividade) - apenas para usuários logados
  useSessionTimeout(user ? 30 : 0);
  
  // Hook para verificar expiração do trial
  useTrialExpiration();

  // Se o usuário precisa trocar a senha E é seu primeiro login, mostrar componente de troca obrigatória
  if (user && user.needsPasswordChange && user.isFirstLogin && user.role === 'company') {
    return <ForcePasswordChange onPasswordChanged={refreshUser} />;
  }

  return (
    <Routes>
      {/* Home Route */}
      <Route path="/" element={<Home />} />
      
      {/* Public Routes */}
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/trial-registration" element={<TrialRegistration />} />
      <Route path="/public-search" element={<Navigate to="/customer-auth" replace />} />
      <Route path="/cadastro-cliente/:companyId" element={<PublicCustomerRegistration />} />
      <Route path="/test/:companyId" element={<PublicTestPage />} />
      <Route path="/test-referral" element={<ReferralSystemTest />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <Layout>
            <AdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/companies" element={
        <ProtectedRoute requiredRole="admin">
          <Layout>
            <CompaniesManager />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/history" element={
        <ProtectedRoute requiredRole="admin">
          <Layout>
            <AdminHistory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/points" element={
        <ProtectedRoute requiredRole="admin">
          <Layout>
            <AdminPointsView />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute requiredRole="admin">
          <Layout>
            <AdminSettings />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Company Routes */}
      <Route path="/company" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <CompanyDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/customers" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <CustomersManager />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/points" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <AddPointsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/history" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <PointsHistory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/search" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <PointsSearch />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/rewards" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <RewardsManager />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/rewards-history" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <RewardsHistory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/redemptions" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <RedemptionsManager />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/rentals" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <RentalManager />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/customer-history" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <CustomerPointsHistory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/force-redemption" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <ForceRedemption />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/customer-history" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <CustomerPointsHistory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/settings" element={
        <ProtectedRoute requiredRole="company">
          <Layout>
            <CompanySettings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/sms-settings" element={
        <ProtectedRoute requiredRole="admin">
          <Layout>
            <SMSSettings />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Rotas de Cliente */}
      <Route path="/customer-auth" element={<CustomerAuth />} />
      <Route path="/customer-dashboard" element={
        <CustomerProtectedRoute>
          <CustomerDashboard />
        </CustomerProtectedRoute>
      } />
      
      {/* Redirect unknown routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <CustomerAuthProvider>
          <CompanyProvider>
            <CustomerProvider>
              <AppContent />
            </CustomerProvider>
          </CompanyProvider>
        </CustomerAuthProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
